/**
 * 데이터비교 노드의 비교 로직(항목 단위 규칙) — 규칙 기반 생성과 AI 결과 검증.
 *
 * 규칙 하나 = 「타겟 항목 ← 원천 항목 또는 식」 + 정규화. 식은 매핑 식과 같은 문법이고, 서버(data_compare.py)가
 * 원천 행에 식을 적용한 기대값을 타겟 값과 비교한다.
 *
 * 규칙 기반 생성(generateCompareRules): 원천 데이터 노드에서 타겟 데이터 노드까지 이어진 흐름을 찾아
 * 경로의 데이터처리 매핑을 차례로 **합성**한다(B = upper(A), C = substr(B,1,3) → C = substr((upper(A)),1,3)).
 * 필터·정렬·품질검사·룩업은 항목을 그대로 넘기고, 조인·집계를 만나면 그 뒤로는 이름으로 짝짓는다.
 * 경로가 없으면 이름이 같은 항목끼리(대소문자 무시). 무엇을 근거로 했는지는 notes 와 규칙의 note 로 남긴다.
 */
import i18n from '../../../i18n';
import type { Column } from './transformLoad';

const tr = (k: string, o: Record<string, unknown> = {}) => String(i18n.t(`workflow:compare.${k}`, o as any));

export interface CompareRule {
  target: string;
  source?: string;
  expr?: string;
  trim?: boolean;
  ignoreCase?: boolean;
  numeric?: boolean;
  tolerance?: number;
  date?: boolean;
  enabled?: boolean;
  origin?: 'rule' | 'ai' | 'user';
  note?: string;
}

export interface CompareKey { source: string; target: string }

/** 서버 매핑 식에서 쓸 수 있는 함수(task_functions._EXPR_FUNCS) — AI 식 검증에 쓴다 */
export const EXPR_FUNCS = new Set(['abs', 'coalesce', 'concat', 'float', 'iif', 'int', 'len', 'length', 'lower', 'lpad', 'max', 'min',
  'now', 'nvl', 'replace', 'round', 'rpad', 'str', 'substr', 'to_int', 'to_num', 'to_str', 'today', 'trim', 'upper']);

const IDENT = /[A-Za-z_가-힣][\w가-힣]*/g;
const up = (s: string) => s.toUpperCase();

/** 글자 상수('…'/"…") 밖의 식별자 — [이름, 함수 호출인가] */
export function exprIdentifiers(expr: string): Array<[string, boolean]> {
  const out: Array<[string, boolean]> = [];
  const stripped = expr.replace(/'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"/g, (m) => ' '.repeat(m.length));
  let m: RegExpExecArray | null;
  IDENT.lastIndex = 0;
  while ((m = IDENT.exec(stripped))) {
    const after = stripped.slice(m.index + m[0].length).trimStart();
    out.push([m[0], after.startsWith('(')]);
  }
  return out;
}

/** 식 안의 항목 이름을 다른 식으로 바꾼다(합성) — 함수 이름·글자 상수는 건드리지 않는다 */
function substitute(expr: string, env: Record<string, string>): string {
  const parts = expr.split(/('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")/);
  return parts.map((p, i) => (i % 2 === 1 ? p : p.replace(IDENT, (id, off: number, whole: string) => {
    const after = whole.slice(off + id.length).trimStart();
    if (after.startsWith('(')) return id;                 // 함수
    const e = env[up(id)];
    if (e === undefined) return id;
    return /^[A-Za-z_가-힣][\w가-힣]*$/.test(e) ? e : `(${e})`;
  }))).join('');
}

/** 식이 원천 항목과 허용 함수만 쓰는가 — 아니면 사유 */
export function checkExpr(expr: string, sourceCols: string[]): string | null {
  const cols = new Set(sourceCols.map(up));
  for (const [id, isCall] of exprIdentifiers(expr)) {
    if (isCall) {
      if (!EXPR_FUNCS.has(id.toLowerCase())) return tr('badFunc', { name: id });
    } else if (!cols.has(up(id)) && !['TRUE', 'FALSE', 'NONE', 'AND', 'OR', 'NOT', 'IF', 'ELSE', 'IN'].includes(up(id))) {
      return tr('noSourceCol', { name: id });
    }
  }
  return null;
}

const NUMERIC = /NUMBER|NUMERIC|DECIMAL|INT|FLOAT|DOUBLE|REAL|MONEY/i;
const DATELIKE = /DATE|TIME/i;

function defaultsFor(targetType?: string): Partial<CompareRule> {
  return { trim: true, ...(targetType && NUMERIC.test(targetType) ? { numeric: true } : {}), ...(targetType && DATELIKE.test(targetType) ? { date: true } : {}) };
}

/** 원천 → 타겟 흐름 경로(노드 id 목록). 데이터비교로 들어가는 점선은 흐름이 아니므로 따라가지 않는다 */
export function findPath(fromId: string, toId: string, nodes: any[], edges: any[]): string[] | null {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const prev = new Map<string, string>();
  const q = [fromId];
  const seen = new Set([fromId]);
  while (q.length) {
    const x = q.shift()!;
    if (x === toId) {
      const path = [x];
      while (prev.has(path[0])) path.unshift(prev.get(path[0])!);
      return path;
    }
    for (const e of edges) {
      if (e.source !== x || seen.has(e.target)) continue;
      if (byId.get(e.target)?.type === 'compare') continue;
      seen.add(e.target);
      prev.set(e.target, x);
      q.push(e.target);
    }
  }
  return null;
}

const PASS_COLUMNS = new Set(['innerData', 'filter', 'sort', 'quality', 'lookup', 'branch', 'merge', 'userConfirm', 'csvData', 'rdbData', 'group', 'subflow', 'repeatGroup']);

export interface GenerateResult { rules: CompareRule[]; keys: CompareKey[]; notes: string[] }

/**
 * 규칙 기반 생성. sourceCols·targetCols 는 지금 알 수 있는 항목(비면 경로의 매핑만으로 만든다).
 * keys 가 비어 있으면 경로의 UPSERT 키를 원천 항목으로 되돌려 제안한다.
 */
export function generateCompareRules(sourceId: string, targetId: string, nodes: any[], edges: any[],
  sourceCols: Column[], targetCols: Column[], keys: CompareKey[]): GenerateResult {
  const notes: string[] = [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const path = findPath(sourceId, targetId, nodes, edges);
  // env: 지금 단계의 항목 이름(대문자) → 원천 기준 식
  let env: Record<string, string> | null = Object.fromEntries(sourceCols.map((c) => [up(c.name), c.name]));
  let upsertKeys: string[] = [];
  let broken = '';
  if (!path) {
    notes.push(tr('noPath'));
    env = null;
  } else {
    for (const id of path.slice(1, -1)) {
      const n = byId.get(id);
      if (!n) continue;
      const cfg = n.data?.config || {};
      if (n.type === 'transformLoad') {
        if (String(cfg.mode || '').toUpperCase() === 'UPSERT' && Array.isArray(cfg.keyColumns)) upsertKeys = cfg.keyColumns;
        if (String(cfg.transformMode || '').toUpperCase() !== 'MAPPING' || !Array.isArray(cfg.mapping) || !cfg.mapping.length) continue;
        const next: Record<string, string> = {};
        for (const m of cfg.mapping) {
          if (!m?.target) continue;
          const expr = String(m.expr || '').trim();
          if (expr) next[up(m.target)] = env ? substitute(expr, env) : expr;
          else if (m.source) next[up(m.target)] = env?.[up(m.source)] ?? m.source;
        }
        env = next;
      } else if (!PASS_COLUMNS.has(n.type)) {
        broken = `${n.data?.label || n.type}(${n.type})`;
        break;
      }
    }
    if (broken) {
      notes.push(tr('pathBroken', { node: broken }));
      env = null;
    } else {
      notes.push(tr('pathFollowed', { n: path.length - 2 }));
    }
  }
  const srcNames = new Map(sourceCols.map((c) => [up(c.name), c.name]));
  const targets: Column[] = targetCols.length ? targetCols : Object.keys(env || {}).map((n) => ({ name: n }));
  const keyT = new Set(keys.map((k) => up(k.target)));
  const rules: CompareRule[] = [];
  for (const tc of targets) {
    if (keyT.has(up(tc.name))) continue;
    const via = env?.[up(tc.name)];
    const base = { target: tc.name, ...defaultsFor(tc.dataType), enabled: true, origin: 'rule' as const };
    if (via !== undefined) {
      const bare = /^[A-Za-z_가-힣][\w가-힣]*$/.test(via);
      if (bare && (!sourceCols.length || srcNames.has(up(via)))) rules.push({ ...base, source: srcNames.get(up(via)) || via, note: tr('noteFlow') });
      else if (!bare) rules.push({ ...base, expr: via, note: tr('noteFlowExpr') });
      else rules.push({ ...base, source: via, enabled: false, note: tr('noteMissing', { name: via }) });
    } else if (srcNames.has(up(tc.name))) {
      rules.push({ ...base, source: srcNames.get(up(tc.name))!, note: tr('noteName') });
    }
  }
  const missing = targets.filter((tc) => !keyT.has(up(tc.name)) && !rules.some((r) => up(r.target) === up(tc.name))).map((c) => c.name);
  if (missing.length) notes.push(tr('unmatchedTargets', { names: missing.join(', ') }));
  let outKeys = keys;
  if (!keys.length && upsertKeys.length) {
    outKeys = upsertKeys.map((k) => ({ target: k, source: env?.[up(k)] && /^[\w가-힣]+$/.test(env[up(k)]) ? env[up(k)] : (srcNames.get(up(k)) || k) }));
    notes.push(tr('keysFromUpsert', { names: upsertKeys.join(', ') }));
  }
  return { rules, keys: outKeys, notes };
}

/** AI 제안 검증 — 없는 항목·쓸 수 없는 식은 사유와 함께 버린다(조용히 버리지 않는다) */
export function validateAiRules(raw: any[], sourceCols: string[], targetCols: string[]): { kept: CompareRule[]; dropped: string[] } {
  const kept: CompareRule[] = [];
  const dropped: string[] = [];
  const tset = new Map(targetCols.map((c) => [up(c), c]));
  const sset = new Map(sourceCols.map((c) => [up(c), c]));
  for (const r of raw || []) {
    const target = String(r?.target || '');
    if (!target || (targetCols.length && !tset.has(up(target)))) { dropped.push(tr('dropNoTarget', { target: target || '?' })); continue; }
    const expr = String(r?.expr || '').trim();
    const source = String(r?.source || '').trim();
    if (expr) {
      const why = checkExpr(expr, sourceCols);
      if (why) { dropped.push(tr('dropBadExpr', { target, why, expr })); continue; }
    } else if (!source || (sourceCols.length && !sset.has(up(source)))) {
      dropped.push(tr('dropNoSource', { target, source: source || '-' })); continue;
    }
    kept.push({
      target: tset.get(up(target)) || target, ...(expr ? { expr } : { source: sset.get(up(source)) || source }),
      trim: true, ignoreCase: !!r.ignoreCase, numeric: !!r.numeric, date: !!r.date,
      ...(r.tolerance ? { tolerance: Number(r.tolerance) || 0 } : {}),
      enabled: false, origin: 'ai', note: String(r.reason || '').slice(0, 200),
    });
  }
  return { kept, dropped };
}
